import { useLingui } from "@lingui/react/macro";
import { useFormContext, useWatch, type FieldValues } from "react-hook-form";
import { Button, Disclosure, DisclosurePanel } from "react-aria-components";
import { IconButton } from "@medoma/components/Button/Button";
import BinIcon from "@medoma/components/icons/BinIcon";
import CaretRightIcon from "@medoma/components/icons/CaretRightIcon";
import { CategoryIcon } from "@/components/CategoryIcon/CategoryIcon";
import type { IActivityCategory } from "@medoma/models/activities";
import type { IPatientId } from "@medoma/models/patients";
import { ActivityFields } from "./ActivityFields";
import styles from "./RegisterActivityRetroactivelyForm.module.scss";

type ActivityPanelProps = {
  id: string;
  index: number;
  patientId: IPatientId;
  category: IActivityCategory;
  onRemove?: () => void;
};

export const ActivityPanel = ({
  id,
  index,
  patientId,
  category,
  onRemove,
}: ActivityPanelProps) => {
  const { t } = useLingui();
  const { control } = useFormContext<FieldValues>();
  const title = useWatch({ control, name: `activities.${index}.title` });
  const displayTitle = title || t`Aktivitet`;
  const canRemove = onRemove !== undefined;

  return (
    <Disclosure id={id} className={styles.activityPanel}>
      <div className={styles.activityPanelHeader}>
        <Button
          slot="trigger"
          className={styles.expandTrigger}
          aria-label={displayTitle}
        >
          <CaretRightIcon />
        </Button>
        <span className={styles.headerTitle}>
          <CategoryIcon category={category} size="small" />
          {displayTitle}
        </span>
        {canRemove ? (
          <IconButton
            aria-label={t`Ta bort ${displayTitle}`}
            type="button"
            onClick={onRemove}
          >
            <BinIcon />
          </IconButton>
        ) : null}
      </div>
      <DisclosurePanel>
        <section className={styles.activityPanelContent}>
          <ActivityFields
            patientId={patientId}
            category={category}
            namePrefix={`activities.${index}`}
          />
        </section>
      </DisclosurePanel>
    </Disclosure>
  );
};
